import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Certificate from '@/components/Certificate/Certificate';

const PRINT_STYLES = `
  @page {
    size: landscape;
    margin: 0;
  }
  @media print {
    body:has(#printable-certificates) > *:not(#printable-certificates) {
      display: none !important;
    }
    #printable-certificates {
      display: block !important;
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 100% !important;
    }
    .certificate-page {
      page-break-after: always;
      page-break-inside: avoid;
      break-after: page;
      break-inside: avoid;
      width: 11in;
      height: 8.5in;
      overflow: hidden;
      box-sizing: border-box;
    }
    .certificate-page:last-child {
      page-break-after: auto;
      break-after: auto;
    }
  }
`;

export interface PrintableCertificateData {
  planName: string;
  teamMemberName: string;
  completionDateISO: string;
  expirationDateISO?: string;
}

interface PrintableCertificatesProps {
  certificates: PrintableCertificateData[];
  onReady?: () => void;
  onProgress?: (progress: number) => void;
}

/**
 * Hidden container that renders certificates for printing.
 *
 * Portal is required because:
 * 1. The `:has()` selector in `body:has(#printable-certificates)` only works
 *    when the element is a direct child of body
 * 2. Rendering inside deeply nested components would break the CSS selector
 *    used to hide all other content during print
 * 3. Portal ensures consistent print behavior regardless of where this
 *    component is mounted in the React tree
 */
export const PrintableCertificates: React.FC<PrintableCertificatesProps> = ({
  certificates,
  onReady,
  onProgress,
}) => {
  const { i18n } = useTranslation();
  const [readyCount, setReadyCount] = useState(0);
  const [prevCertificates, setPrevCertificates] = useState(certificates);
  const hasNotifiedRef = useRef(false);
  const onProgressRef = useRef(onProgress);

  if (prevCertificates !== certificates) {
    setPrevCertificates(certificates);
    setReadyCount(0);
  }

  useLayoutEffect(() => {
    onProgressRef.current = onProgress;
  });

  // Notify parent when all certificates are ready
  const allReady = readyCount >= certificates.length && certificates.length > 0;

  useEffect(() => {
    if (allReady && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      onReady?.();
    }
    if (!allReady) {
      hasNotifiedRef.current = false;
    }
  }, [allReady, onReady]);

  const handleCertificateReady = useCallback(() => {
    setReadyCount(prev => {
      const newCount = prev + 1;
      const total = certificates.length;
      if (total > 0) {
        onProgressRef.current?.(newCount / total);
      }
      return newCount;
    });
  }, [certificates]);

  // Don't render portal if no certificates
  if (certificates.length === 0) {
    return null;
  }

  return createPortal(
    <>
      <style>{PRINT_STYLES}</style>
      <div
        id="printable-certificates"
        className="fixed top-0 left-[-200vw] w-screen"
        data-testid="printable-certificates"
      >
        {certificates.map(cert => (
          <div
            key={`${cert.teamMemberName}-${cert.planName}-${cert.completionDateISO}`}
            className="certificate-page"
          >
            <Certificate
              planName={cert.planName}
              teamMemberName={cert.teamMemberName}
              completionDate={cert.completionDateISO}
              expirationDate={cert.expirationDateISO}
              language={i18n.language}
              onReady={handleCertificateReady}
              forPrint
              className="size-full"
            />
          </div>
        ))}
      </div>
    </>,
    document.body,
  );
};
